// Owner-supplied planning ranges per 100 g of dry powder; not laboratory results.
export const NUTRIENTS = [
  { id: "energy", label: "Energy / Calories", unit: "kcal", min: 340, max: 371 },
  { id: "carbs", label: "Carbohydrates", unit: "g", min: 60, max: 70.8 },
  { id: "protein", label: "Protein", unit: "g", min: 9.5, max: 12.5 },
  { id: "fiber", label: "Dietary fibre", unit: "g", min: 6.6, max: 11 },
  { id: "fat", label: "Total fat", unit: "g", min: 2.8, max: 7.5 },
  { id: "calcium", label: "Calcium", unit: "mg", min: 14, max: 166 },
  { id: "iron", label: "Iron", unit: "mg", min: 3.5, max: 5.5 },
];

export const PACK_SIZES = [100, 250, 500, 1000];
export const SERVING_SIZES = [30, 45];
export const quantityLabel = (grams) => grams === 1000 ? "1 kg" : `${grams} g`;
export const formatNumber = (value) => new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
}).format(Math.round((value + Number.EPSILON) * 100) / 100);

export const nutritionRange = (id, grams, extra = 0) => {
  const nutrient = NUTRIENTS.find((item) => item.id === id);
  return [nutrient.min, nutrient.max].map((value) => value * grams / 100 + extra);
};

export const formatRange = (id, grams, extra = 0) => {
  const nutrient = NUTRIENTS.find((item) => item.id === id);
  const [min, max] = nutritionRange(id, grams, extra);
  return `${formatNumber(min)} – ${formatNumber(max)} ${nutrient.unit}`;
};

// Values are for the specified portions, not per gram. Honey and buttermilk
// estimates are the additions implied by the owner's per-bowl comparisons.
export const ADD_INS = [
  { id: "whole-milk", label: "Whole milk", portion: "100 ml", energy: 62, protein: 3.2, carbs: 4.8 },
  { id: "skimmed-milk", label: "Skimmed milk", portion: "100 ml", energy: 35, protein: 3.4, carbs: 5 },
  { id: "jaggery", label: "Organic jaggery", portion: "12 g · about 1 tbsp", energy: 45, protein: 0, carbs: 11 },
  { id: "sugar", label: "White sugar", portion: "12 g · about 1 tbsp", energy: 48, protein: 0, carbs: 12 },
  { id: "honey", label: "Honey", portion: "7 g · about 1 tsp", energy: 21, protein: 0, carbs: 6 },
  { id: "buttermilk", label: "Diluted curd / buttermilk", portion: "100 ml", energy: 40, protein: 3.3, carbs: 4.7 },
];

export const RECIPES = [
  { id: "water", title: "Water-only bowl", note: "No added sweetener", ingredients: ["200 ml water", "No added sugar"], additions: [] },
  { id: "traditional", title: "Traditional milk & jaggery", note: "Classic Andhra-style sweetness", ingredients: ["150 ml whole milk + 50 ml water", "12 g organic jaggery (about 1 tbsp)"], additions: [["whole-milk", 1.5], ["jaggery", 1]] },
  { id: "lean", title: "Skimmed milk & honey", note: "Higher-protein milk option", ingredients: ["200 ml skimmed milk", "7 g honey (about 1 tsp)"], additions: [["skimmed-milk", 2], ["honey", 1]] },
  { id: "savoury", title: "Savoury buttermilk bowl", note: "A savoury summer option", ingredients: ["150 ml water to cook", "100 ml diluted curd / buttermilk", "A pinch of salt & cumin"], additions: [["buttermilk", 1]] },
];

export const recipeRange = (recipe, id, grams) => {
  const extra = recipe.additions.reduce((sum, [addInId, portions]) => {
    return sum + ADD_INS.find((item) => item.id === addInId)[id] * portions;
  }, 0);
  return formatRange(id, grams, extra);
};

const macroDefinitions = [
  { id: "carbs", name: "Carbohydrates", factor: 4, color: "#C85A32" },
  { id: "protein", name: "Protein", factor: 4, color: "#2D4A3E" },
  { id: "fat", name: "Fat", factor: 9, color: "#E5982B" },
];
const macroEnergy = macroDefinitions.map((macro) => {
  const nutrient = NUTRIENTS.find((item) => item.id === macro.id);
  return { ...macro, grams: (nutrient.min + nutrient.max) / 2, value: (nutrient.min + nutrient.max) / 2 * macro.factor };
});
const totalMacroEnergy = macroEnergy.reduce((sum, item) => sum + item.value, 0);
export const MACROS = macroEnergy.map((item) => ({
  ...item, percent: Math.round(item.value / totalMacroEnergy * 1000) / 10,
}));